import { injectable } from '@servicetitan/react-ioc';
import { CancelToken } from 'axios';

import { HttpClient, Response } from './http-client';
import { BaseRequestModel } from '../models/base-request.model';

@injectable()
export class EquipmentsApi {
    constructor(private readonly httpClient: HttpClient) {}

    getLocationEquipments = async (
        data: GetEquipmentRequestModel,
        cancellationToken?: CancelToken
    ) => {
        return this.httpClient.post<InstalledEquipmentsApiResponse, GetEquipmentRequestModel>(
            '/location_equipments',
            data,
            {
                cancelToken: cancellationToken,
            }
        );
    };

    getEquipmentManufacturers = async (
        data: SearchManufacturerRequestModel,
        cancellationToken?: CancelToken
    ): Response<SearchManufacturerResponseApiModel> => {
        return this.httpClient.post<
            SearchManufacturerResponseApiModel,
            SearchManufacturerRequestModel
        >('/manufacturer_search', data, {
            cancelToken: cancellationToken,
        });
    };

    getEquipmentModelSeries = async (
        data: SearchModelSeriesRequestModel,
        cancellationToken?: CancelToken
    ): Response<SearchModelSeriesResponseApiModel> => {
        return this.httpClient.post<
            SearchModelSeriesResponseApiModel,
            SearchModelSeriesRequestModel
        >('/model_series_search', data, {
            cancelToken: cancellationToken,
        });
    };

    getEquipmentModelNumbers = async (
        data: SearchModelNumberRequestModel,
        cancellationToken?: CancelToken
    ): Response<SearchModelNumberResponseApiModel> => {
        return this.httpClient.post<
            SearchModelNumberResponseApiModel,
            SearchModelNumberRequestModel
        >('/model_number_search', data, {
            cancelToken: cancellationToken,
        });
    };

    getEquipmentInfo = async (
        data: EquipmentInfoRequestModel,
        cancellationToken?: CancelToken
    ): Response<EquipmentInfoResponseApiModel> => {
        return this.httpClient.post<EquipmentInfoResponseApiModel, EquipmentInfoRequestModel>(
            '/equipment_info',
            data,
            {
                cancelToken: cancellationToken,
            }
        );
    };

    loadPdfUrls = (data: GetDocumentUrlRequestModel, cancellationToken?: CancelToken) => {
        return this.httpClient.post<EquipmentDocumentsWithUrlsApiModel, GetDocumentUrlRequestModel>(
            '/document_file_retriever',
            data,
            {
                cancelToken: cancellationToken,
            }
        );
    };
}

export interface GetDocumentUrlRequestModel extends BaseRequestModel {
    documents: DocumentIfNamePair[];
    location_id?: number;
}

export interface DocumentIfNamePair {
    id: string;
    download_file_name: string;
}

export interface EquipmentDocumentsWithUrlsApiModel {
    document_files: EquipmentDocumentApiModel[];
}

// -----------------------------------

export interface EquipmentInfoRequestModel extends BaseRequestModel {
    location_id: number;
    equipment_id?: number;
    mapped_equipment_ids?: string[];
}

export interface EquipmentInfoResponseApiModel {
    mapped_equipments: MappedEquipmentApiModel[];
}

export interface MappedEquipmentApiModel {
    id: string;
    name: string;
    manufacturer: {
        text: string;
        id: string;
    };
    documents: EquipmentDocumentApiModel[];
}

export interface EquipmentDocumentApiModel {
    id: string;
    name: string;
    doc_type: string;
    doc_path: string;
    file_type: string;
    download_file_name?: string;
    provider: string;
    is_indexed: boolean;
    url?: string;
}

// --------------Manufacturer

interface SearchManufacturerRequestModel extends BaseRequestModel {
    location_id: number;
    number_of_response_items: number;
    search_text?: string;
}

export interface SearchManufacturerResponseApiModel {
    manufacturer_candidates: ManufacturerApiModel[];
}

export interface ManufacturerApiModel {
    id: string;
    text: string;
}

// --------------Model Series

interface SearchModelSeriesRequestModel extends BaseRequestModel {
    location_id: number;
    search_text?: string;
    number_of_response_items: number;
    manufacturer_selection?: ManufacturerApiModel;
}

export interface SearchModelSeriesResponseApiModel {
    model_series_candidates: ModelSeriesApiModel[];
}

export interface ModelSeriesApiModel {
    id: string;
    text: string;
    manufacturer: ManufacturerApiModel;
}

// --------------Model Number

interface SearchModelNumberRequestModel extends BaseRequestModel {
    location_id: number;
    search_text?: string;
    number_of_response_items: number;
    manufacturer_selection?: ManufacturerApiModel;
    model_series_selection?: ModelSeriesApiModel;
}

export interface SearchModelNumberResponseApiModel {
    model_number_candidates: ModelNumberApiModel[];
}

export interface ModelNumberApiModel {
    id: string;
    text: string;
    manufacturer_image: string;
    model_notes: string | null;
    model_image: string;
    has_document_coverage: boolean;
    model_series: ModelSeriesApiModel;
}

// ----------------------------------

interface GetEquipmentRequestModel extends BaseRequestModel {
    location_id: number;
}

export interface InstalledEquipmentsApiResponse {
    equipments: InstalledEquipmentApiModel[];
}

export interface InstalledEquipmentApiModel {
    equipment_id: number;
    name: string | null;
    status: string;
    equipment_type: null;
    cost: number;

    manufacturer: string;
    model: string;
    serial_number: null;

    location_id: number;
    invoice_item_id: number | null;
    installed_date: string;
    last_service_date: null;

    has_document_coverage?: boolean;
    mapped_equipment_ids?: string[];

    label: string;
    disabled: boolean;
}
